import { useQuery } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { Bell, Calendar, Clock, CheckCircle, AlertCircle, Mail, MessageSquare, AlertTriangle, Heart, Home, Car, Apple, Zap, Users, Activity } from "lucide-react";
import { MemberSidebar } from "@/components/layout/MemberSidebar";
import { MemberHeader } from "@/components/layout/MemberHeader";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface Reminder {
  id: string;
  session_id: string;
  reminder_type: string;
  sent_at: string;
  delivery_method: string;
  status: string;
  session?: {
    scheduled_date: string;
    scheduled_time: string;
    visit_type: string;
    provider_name?: string;
  };
}

interface UpcomingAppointment {
  id: string;
  scheduled_date: string;
  scheduled_time: string;
  visit_type: string;
  status: string;
  provider_id: string;
  provider_name?: string;
}

interface HealthAlert {
  id: string;
  alert_type: string;
  metric: string;
  value: string;
  threshold: string;
  is_acknowledged: boolean;
  created_at: string;
}

const getAlertIcon = (metric: string) => {
  const lower = metric.toLowerCase();
  if (lower.includes("food")) return <Apple className="h-5 w-5" />;
  if (lower.includes("housing")) return <Home className="h-5 w-5" />;
  if (lower.includes("transport")) return <Car className="h-5 w-5" />;
  if (lower.includes("social") || lower.includes("isolation")) return <Users className="h-5 w-5" />;
  if (lower.includes("utility")) return <Zap className="h-5 w-5" />;
  if (lower.includes("safety")) return <AlertTriangle className="h-5 w-5" />;
  if (lower.includes("heart") || lower.includes("bp") || lower.includes("blood pressure")) return <Heart className="h-5 w-5" />;
  if (lower.includes("glucose") || lower.includes("spo2") || lower.includes("oxygen")) return <Activity className="h-5 w-5" />;
  return <AlertTriangle className="h-5 w-5" />;
};

const getAlertColor = (alertType: string) => {
  switch (alertType) {
    case "sdoh":
      return "bg-amber-500/10 text-amber-600 border-amber-500/20";
    case "rpm":
    case "vital":
      return "bg-destructive/10 text-destructive border-destructive/20";
    default:
      return "bg-primary/10 text-primary border-primary/20";
  }
};

const getAlertTypeLabel = (alertType: string) => {
  switch (alertType) {
    case "sdoh":
      return "SDOH";
    case "rpm":
    case "vital":
      return "RPM";
    default:
      return alertType.toUpperCase();
  }
};

export default function MemberNotifications() {
  const { user } = useAuth();

  // Fetch health alerts
  const { data: healthAlerts, isLoading: alertsLoading } = useQuery({
    queryKey: ["member-health-alerts", user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from("health_alerts")
        .select("*")
        .eq("member_id", user.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data || []) as HealthAlert[];
    },
    enabled: !!user,
  });

  const unacknowledgedAlerts = healthAlerts?.filter((a) => !a.is_acknowledged) || [];

  // Fetch past reminders
  const { data: reminders, isLoading: remindersLoading } = useQuery({
    queryKey: ["member-reminders", user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data: sessions } = await supabase
        .from("telehealth_sessions")
        .select("id")
        .eq("member_id", user.id);

      if (!sessions || sessions.length === 0) return [];

      const sessionIds = sessions.map((s) => s.id);

      const { data, error } = await supabase
        .from("appointment_reminders")
        .select("*")
        .in("session_id", sessionIds)
        .order("sent_at", { ascending: false });

      if (error) throw error;

      const remindersWithSessions = await Promise.all(
        (data || []).map(async (reminder) => {
          const { data: session } = await supabase
            .from("telehealth_sessions")
            .select("scheduled_date, scheduled_time, visit_type, provider_id")
            .eq("id", reminder.session_id)
            .single();

          let providerName = "Provider";
          if (session?.provider_id) {
            const { data: provider } = await supabase
              .from("providers")
              .select("display_name")
              .eq("user_id", session.provider_id)
              .maybeSingle();
            providerName = provider?.display_name || "Provider";
          }

          return {
            ...reminder,
            session: session ? { ...session, provider_name: providerName } : undefined,
          };
        })
      );

      return remindersWithSessions as Reminder[];
    },
    enabled: !!user,
  });

  // Fetch upcoming appointments
  const { data: upcomingAppointments, isLoading: appointmentsLoading } = useQuery({
    queryKey: ["upcoming-appointments", user?.id],
    queryFn: async () => {
      if (!user) return [];

      const today = format(new Date(), "yyyy-MM-dd");

      const { data, error } = await supabase
        .from("telehealth_sessions")
        .select("*")
        .eq("member_id", user.id)
        .gte("scheduled_date", today)
        .in("status", ["scheduled", "waiting"])
        .order("scheduled_date", { ascending: true })
        .order("scheduled_time", { ascending: true });

      if (error) throw error;

      const appointmentsWithProviders = await Promise.all(
        (data || []).map(async (apt) => {
          const { data: provider } = await supabase
            .from("providers")
            .select("display_name")
            .eq("user_id", apt.provider_id)
            .maybeSingle();

          return { ...apt, provider_name: provider?.display_name || "Provider" };
        })
      );

      return appointmentsWithProviders as UpcomingAppointment[];
    },
    enabled: !!user,
  });

  const getReminderTypeLabel = (type: string) => {
    switch (type) {
      case "24_hour": return "24 Hour Reminder";
      case "1_hour": return "1 Hour Reminder";
      case "15_min": return "15 Minute Reminder";
      default: return type;
    }
  };

  const getDeliveryIcon = (method: string) => {
    switch (method) {
      case "email": return <Mail className="h-4 w-4" />;
      case "sms": return <MessageSquare className="h-4 w-4" />;
      default: return <Bell className="h-4 w-4" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "sent":
        return (
          <Badge variant="default" className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20">
            <CheckCircle className="h-3 w-3 mr-1" />
            Sent
          </Badge>
        );
      case "failed":
        return (
          <Badge variant="destructive">
            <AlertCircle className="h-3 w-3 mr-1" />
            Failed
          </Badge>
        );
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const formatVisitType = (type: string) => {
    return type.split("_").map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
  };

  const renderLoadingSkeleton = () => (
    <div className="space-y-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex items-center gap-4 p-4 rounded-lg border border-border/50">
          <Skeleton className="h-12 w-12 rounded-lg" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <MemberSidebar />
      <div className="ml-64">
        <MemberHeader />
        <main className="p-6">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <Bell className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h1 className="font-display text-3xl font-bold text-foreground">Notifications Center</h1>
                <p className="mt-1 text-muted-foreground">
                  View your health alerts, appointment reminders, and upcoming visits
                </p>
              </div>
            </div>
          </div>

          <Tabs defaultValue="alerts" className="space-y-6">
            <TabsList className="grid w-full max-w-2xl grid-cols-3">
              <TabsTrigger value="alerts" className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                Health Alerts
                {unacknowledgedAlerts.length > 0 && (
                  <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-[10px] font-semibold text-destructive-foreground">
                    {unacknowledgedAlerts.length}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="upcoming" className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Upcoming ({upcomingAppointments?.length || 0})
              </TabsTrigger>
              <TabsTrigger value="reminders" className="flex items-center gap-2">
                <Bell className="h-4 w-4" />
                Reminders ({reminders?.length || 0})
              </TabsTrigger>
            </TabsList>

            {/* Health Alerts Tab */}
            <TabsContent value="alerts">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-primary" />
                    Health Alerts
                  </CardTitle>
                  <CardDescription>
                    Social determinants of health, vital sign alerts, and other health notifications
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {alertsLoading ? (
                    renderLoadingSkeleton()
                  ) : healthAlerts && healthAlerts.length > 0 ? (
                    <ScrollArea className="h-[500px] pr-4">
                      <div className="space-y-4">
                        {healthAlerts.map((alert) => (
                          <div
                            key={alert.id}
                            className={`flex items-start gap-4 p-4 rounded-lg border transition-colors ${
                              alert.is_acknowledged
                                ? "border-border/50 bg-muted/30 opacity-70"
                                : "border-border bg-card"
                            }`}
                          >
                            <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-lg ${getAlertColor(alert.alert_type)}`}>
                              {getAlertIcon(alert.metric)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-medium text-foreground">{alert.metric}</p>
                                <Badge variant="outline" className={getAlertColor(alert.alert_type)}>
                                  {getAlertTypeLabel(alert.alert_type)}
                                </Badge>
                                {alert.is_acknowledged && (
                                  <Badge variant="secondary" className="text-[10px]">
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    Acknowledged
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground mt-1">
                                Reported: <span className="font-medium text-foreground/80">{alert.value}</span>
                              </p>
                              <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                                <Clock className="h-3 w-3" />
                                {format(parseISO(alert.created_at), "MMM d, yyyy 'at' h:mm a")}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
                        <CheckCircle className="h-8 w-8 text-muted-foreground" />
                      </div>
                      <h3 className="font-medium text-foreground">No health alerts</h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        You're all clear! Health alerts will appear here if any concerns are detected.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Upcoming Appointments Tab */}
            <TabsContent value="upcoming">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-primary" />
                    Upcoming Appointments
                  </CardTitle>
                  <CardDescription>Your scheduled telehealth visits</CardDescription>
                </CardHeader>
                <CardContent>
                  {appointmentsLoading ? (
                    renderLoadingSkeleton()
                  ) : upcomingAppointments && upcomingAppointments.length > 0 ? (
                    <ScrollArea className="h-[400px] pr-4">
                      <div className="space-y-4">
                        {upcomingAppointments.map((apt) => (
                          <div
                            key={apt.id}
                            className="flex items-center gap-4 p-4 rounded-lg border border-border/50 hover:bg-muted/50 transition-colors"
                          >
                            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                              <Calendar className="h-6 w-6 text-primary" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-foreground">
                                  {formatVisitType(apt.visit_type)} Visit
                                </p>
                                <Badge
                                  variant="outline"
                                  className={
                                    apt.status === "waiting"
                                      ? "bg-amber-500/10 text-amber-600 border-amber-500/20"
                                      : "bg-blue-500/10 text-blue-600 border-blue-500/20"
                                  }
                                >
                                  {apt.status === "waiting" ? "In Waiting Room" : "Scheduled"}
                                </Badge>
                              </div>
                              <p className="text-sm text-muted-foreground">with {apt.provider_name}</p>
                              <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <Calendar className="h-3 w-3" />
                                  {format(parseISO(apt.scheduled_date), "EEEE, MMMM d, yyyy")}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  {apt.scheduled_time}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
                        <Calendar className="h-8 w-8 text-muted-foreground" />
                      </div>
                      <h3 className="font-medium text-foreground">No upcoming appointments</h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        Schedule a visit to see your upcoming appointments here
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Past Reminders Tab */}
            <TabsContent value="reminders">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Bell className="h-5 w-5 text-primary" />
                    Past Reminders
                  </CardTitle>
                  <CardDescription>History of all appointment reminders sent to you</CardDescription>
                </CardHeader>
                <CardContent>
                  {remindersLoading ? (
                    renderLoadingSkeleton()
                  ) : reminders && reminders.length > 0 ? (
                    <ScrollArea className="h-[400px] pr-4">
                      <div className="space-y-4">
                        {reminders.map((reminder) => (
                          <div
                            key={reminder.id}
                            className="flex items-center gap-4 p-4 rounded-lg border border-border/50 hover:bg-muted/50 transition-colors"
                          >
                            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-muted">
                              {getDeliveryIcon(reminder.delivery_method)}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-foreground">
                                  {getReminderTypeLabel(reminder.reminder_type)}
                                </p>
                                {getStatusBadge(reminder.status)}
                              </div>
                              {reminder.session && (
                                <p className="text-sm text-muted-foreground">
                                  {formatVisitType(reminder.session.visit_type)} with {reminder.session.provider_name}
                                </p>
                              )}
                              <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  Sent {format(parseISO(reminder.sent_at), "MMM d, yyyy 'at' h:mm a")}
                                </span>
                                <span className="capitalize flex items-center gap-1">
                                  {getDeliveryIcon(reminder.delivery_method)}
                                  via {reminder.delivery_method}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
                        <Bell className="h-8 w-8 text-muted-foreground" />
                      </div>
                      <h3 className="font-medium text-foreground">No reminders yet</h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        Appointment reminders will appear here once you have scheduled visits
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </main>
      </div>
    </div>
  );
}
